import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useCapabilities } from "@/hooks/use-capabilities";
import { runServerFn } from "@/lib/server-fn";
import { listWaves, listWavePolicies } from "@/lib/wave-policies.functions";
import { previewWaveFromPolicy, createWaveFromPolicy } from "@/lib/waves.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { Layers, Eye, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/pick/waves/")({
  component: WavesList,
});

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  planned: "secondary",
  released: "default",
  in_progress: "default",
  completed: "outline",
  cancelled: "outline",
  draft: "secondary",
};

function WavesList() {
  const { tenantId, facilityId, customerId } = useScope();
  const caps = useCapabilities();
  const list = useServerFn(listWaves);
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("all");
  const [previewOpen, setPreviewOpen] = useState(false);

  const q = useQuery({
    queryKey: ["waves", tenantId, facilityId, customerId, status],
    enabled: !!tenantId,
    queryFn: () => list({
      data: {
        tenantId: tenantId!,
        facilityId: facilityId ?? null,
        customerId: customerId ?? null,
        status: status === "all" ? null : (status as any),
        limit: 100,
      },
    }),
  });

  if (!caps.isLoading && !caps.isEnabled("wave_planning")) {
    return (
      <div className="p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="h-4 w-4" /> Wave Planning is not enabled
            </CardTitle>
            <CardDescription>
              Pick Release is operating in its default mode: orders flow into picking through the
              existing pipeline as soon as they're released. Wave management is an optional
              orchestration layer.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Enable the wave_planning capability for this tenant, then configure policies in{" "}
            <Link to="/app/admin/wave-policies" className="underline">Admin / Wave policies</Link>.
          </CardContent>
        </Card>
      </div>
    );
  }

  const waves = (q.data?.waves ?? []) as any[];
  const counts = (q.data?.counts ?? {}) as Record<string, { planned: number; released: number; removed: number }>;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Layers className="h-5 w-5" /> Pick Release — Waves
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Plan, preview, and release waves of orders to the picking floor. Pickers execute
            through existing methods (single, batch, zone, cluster) — waves only control when
            and how much work is released.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/app/admin/wave-policies">Policies</Link>
          </Button>
          <Button onClick={() => setPreviewOpen(true)} disabled={!tenantId || !facilityId}>
            <Eye className="h-4 w-4" /> Preview &amp; create
          </Button>
        </div>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="planned">Planned</TabsTrigger>
          <TabsTrigger value="released">Released</TabsTrigger>
          <TabsTrigger value="in_progress">In progress</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
        <TabsContent value={status} className="mt-3">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Wave</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Window</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q.isLoading ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Loading…</TableCell></TableRow>
                  ) : waves.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No waves in this view.</TableCell></TableRow>
                  ) : waves.map((w) => {
                    const c = counts[w.id] ?? { planned: 0, released: 0, removed: 0 };
                    const blocked = w.released_explanation?.blocked_gates?.length > 0;
                    return (
                      <TableRow
                        key={w.id}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate({ to: "/app/pick/waves/$waveId", params: { waveId: w.id } })}
                      >
                        <TableCell>
                          <div className="font-medium">{w.name}</div>
                          {blocked && (
                            <div className="text-xs text-destructive flex items-center gap-1 mt-0.5">
                              <ShieldAlert className="h-3 w-3" /> Release blocked
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[w.status] ?? "secondary"} className="capitalize">
                            {String(w.status).replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground tabular-nums">
                          {c.planned} planned · {c.released} released
                          {c.removed > 0 ? ` · ${c.removed} removed` : ""}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {w.window_start ? new Date(w.window_start).toLocaleString() : "—"}
                          {w.window_end ? ` → ${new Date(w.window_end).toLocaleTimeString()}` : ""}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(w.created_at).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {previewOpen && tenantId && facilityId && (
        <PreviewDialog
          tenantId={tenantId}
          facilityId={facilityId}
          onClose={() => setPreviewOpen(false)}
          onCreated={(waveId) => {
            setPreviewOpen(false);
            navigate({ to: "/app/pick/waves/$waveId", params: { waveId } });
          }}
        />
      )}
    </div>
  );
}

function PreviewDialog({
  tenantId, facilityId, onClose, onCreated,
}: {
  tenantId: string;
  facilityId: string;
  onClose: () => void;
  onCreated: (waveId: string) => void;
}) {
  const listPolicies = useServerFn(listWavePolicies);
  const preview = useServerFn(previewWaveFromPolicy);
  const create = useServerFn(createWaveFromPolicy);
  const [policyId, setPolicyId] = useState<string>("");
  const [name, setName] = useState<string>(`Wave ${new Date().toLocaleString()}`);
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const pq = useQuery({
    queryKey: ["wave-policies", tenantId],
    queryFn: () => listPolicies({ data: { tenantId } }),
  });
  const activePolicies = ((pq.data?.policies ?? []) as any[]).filter((p) => p.is_active);

  const doPreview = async () => {
    if (!policyId) { toast.error("Pick a policy"); return; }
    setBusy(true);
    try {
      const r = await runServerFn(
        () => preview({ data: { tenantId, facilityId, policyId, candidateLimit: 500 } }),
        "Previewing wave",
      );
      setResult(r);
    } finally { setBusy(false); }
  };
  const doCreate = async () => {
    if (!policyId) return;
    setBusy(true);
    try {
      const r = await runServerFn(
        () => create({ data: { tenantId, facilityId, policyId, candidateLimit: 500, name } }),
        "Creating wave",
      );
      toast.success("Wave created");
      onCreated(r.waveId);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview &amp; create wave</DialogTitle>
          <DialogDescription>
            Preview is deterministic and has no side effects. Creating a wave persists the plan
            but does not create pick jobs — that happens on release.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Policy</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={policyId}
                onChange={(e) => { setPolicyId(e.target.value); setResult(null); }}
              >
                <option value="">Select…</option>
                {activePolicies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Wave name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={doPreview} disabled={busy || !policyId}>
              <Eye className="h-4 w-4" /> Preview
            </Button>
            <Button onClick={doCreate} disabled={busy || !policyId || !name.trim()}>
              Create wave
            </Button>
          </div>

          {result && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {result.included?.length ?? 0} included · {result.excluded?.length ?? 0} excluded
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[40vh] overflow-y-auto text-xs">
                {(result.included ?? []).slice(0, 25).map((inc: any) => (
                  <div key={inc.order_id} className="border-l-2 border-primary/50 pl-2">
                    <div className="font-mono text-[11px]">{inc.order_id.slice(0, 8)}…</div>
                    <div className="text-muted-foreground">
                      {inc.included_explanation?.headline ?? "Included"}
                    </div>
                  </div>
                ))}
                {(result.excluded ?? []).slice(0, 15).map((ex: any) => (
                  <div key={ex.order_id} className="border-l-2 border-muted pl-2">
                    <div className="font-mono text-[11px] text-muted-foreground">{ex.order_id.slice(0, 8)}…</div>
                    <div className="text-muted-foreground">
                      {ex.excluded_explanation?.headline ?? "Excluded"}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}